import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { FileUpload } from "primereact/fileupload";
import { Toast } from "primereact/toast";
import React, { useRef, useState } from "react";
import DocumentViewerDialog from "./DocumentViewerDialog";
import Spinner from "presentation/components/Shared/Spinner";
import {
  useAttachedDocuments,
  useCreateAttachedDocumentMutation,
  useDeleteAttachedDocumentMutation,
} from "../../hooks/use-query-attached-document";
import { useAuthStore } from "../../stores/use-auth-store";
import { useUserStore } from "../../stores/use-user-store";
import assetRepository from "../../../infra/repositories/asset-repository";
import { usePendingDocumentsStore, PendingDocument } from "../../stores/use-pending-documents-store";

interface AttachedDocumentsTabProps {
  objectType: string;
  objectId?: string;
  tabContentStyle?: React.CSSProperties;
  onViewDoc?: (fileKey: string, title: string) => void;
}

const AttachedDocumentsTab: React.FC<AttachedDocumentsTabProps> = ({
  objectType,
  objectId,
  tabContentStyle,
  onViewDoc,
}) => {
  const { email: userEmail } = useAuthStore();
  const { user } = useUserStore();
  const fileUploadRef = useRef<FileUpload>(null);
  const toast = useRef<Toast>(null);

  const [viewerVisible, setViewerVisible] = useState(false);
  const [viewerTitle, setViewerTitle] = useState("");
  const [viewerUrl, setViewerUrl] = useState<string | null>(null);
  const [viewerLoading, setViewerLoading] = useState(false);

  const canDeleteDocument =
    user?.roles?.some((role) =>
      ["admin", "manager", "finanças", "financas"].includes(role.name.toLowerCase())
    ) || user?.profileType?.toLowerCase() === "admin";

  const { data: documents, isLoading: loadingDocs } = useAttachedDocuments(
    objectType,
    objectId
  );

  const { pendingDocuments, addPendingDocument, removePendingDocument } =
    usePendingDocumentsStore();

  const uploadMutation = useCreateAttachedDocumentMutation();
  const deleteMutation = useDeleteAttachedDocumentMutation();

  const handleUploadDoc = async (event: any) => {
    const file = event.files[0];

    if (!objectId) {
      const doc: PendingDocument = {
        id: "Temp_" + crypto.randomUUID(),
        file,
        objectType,
        name: file.name,
        uploadedAt: new Date(),
      };
      addPendingDocument(doc);
      fileUploadRef.current?.clear();
      toast.current?.show({ severity: 'success', summary: 'Sucesso', detail: 'Documento pendente, será guardado ao gravar.', life: 3000 });
      return;
    }

    try {
      // 1. Upload para o S3 via assetRepository
      const folder = `attached-documents/${objectType.toLowerCase()}/${objectId}`;
      const uploadRes = await assetRepository.uploadAsset(folder, file);

      // 2. Guardar referência na base de dados
      const payload = {
        objectType,
        objectId,
        name: file.name,
        fileKey: uploadRes.filePath,
      };

      await uploadMutation.mutateAsync(payload);
      fileUploadRef.current?.clear();
      toast.current?.show({ severity: 'success', summary: 'Sucesso', detail: 'Documento carregado com sucesso', life: 3000 });
    } catch (error: any) {
      console.error("Error uploading document:", error);
      toast.current?.show({ severity: 'error', summary: 'Erro', detail: error.message || 'Erro ao carregar documento', life: 3000 });
    }
  };

  const handleDeleteDoc = async (docId: string) => {
    if (docId.startsWith("Temp_")) {
      removePendingDocument(docId);
      toast.current?.show({ severity: 'success', summary: 'Sucesso', detail: 'Documento pendente removido', life: 3000 });
      return;
    }
    if (!objectId) return;
    try {
      await deleteMutation.mutateAsync({ id: docId, objectType, objectId });
      toast.current?.show({ severity: 'success', summary: 'Sucesso', detail: 'Documento eliminado com sucesso', life: 3000 });
    } catch (error: any) {
      console.error("Error deleting document:", error);
      toast.current?.show({ severity: 'error', summary: 'Erro', detail: error.message || 'Erro ao eliminar documento', life: 3000 });
    }
  };

  const defaultViewDoc = async (fileKey: string, title: string) => {
    if (!fileKey) return;
    try {
      setViewerTitle(title);
      setViewerVisible(true);
      setViewerLoading(true);

      if (typeof fileKey === 'string' && fileKey.startsWith('http')) {
        setViewerUrl(fileKey);
        setViewerLoading(false);
        return;
      }
      
      const response = await assetRepository.getAssetUrl(fileKey);
      if (response && response.url) {
        setViewerUrl(response.url);
      }
    } catch (error) {
      console.error("Error fetching document URL:", error);
      toast.current?.show({ severity: 'error', summary: 'Erro', detail: 'Erro ao obter documento', life: 3000 });
      setViewerVisible(false);
    } finally {
      setViewerLoading(false);
    }
  };

  const handleView = onViewDoc || defaultViewDoc;

  const dbDocuments = documents || [];
  const pendingDocsForType = pendingDocuments.filter(d => d.objectType === objectType);
  const allDocuments = [...dbDocuments, ...pendingDocsForType];

  return (
    <div style={tabContentStyle || { padding: "1rem" }}>
      <Toast ref={toast} position="bottom-right" />
      <div className="mb-3">
        <div className="flex justify-content-between align-items-center mb-2">
          <h3 className="m-0">Anexos e Documentos</h3>
          <FileUpload
            ref={fileUploadRef}
            mode="basic"
            name="file"
            accept=".pdf,.jpg,.png"
            maxFileSize={10000000} // 10MB
            customUpload
            auto
            uploadHandler={handleUploadDoc}
            chooseLabel="Novo Ficheiro"
            className="w-auto p-button-sm"
            disabled={uploadMutation.isPending}
          />
        </div>
      </div>

      {loadingDocs && objectId ? (
        <Spinner />
      ) : (
        <DataTable
          value={allDocuments}
          paginator
          rows={5}
          className="mt-2 text-sm"
            emptyMessage="Nenhum documento encontrado."
          >
            <Column field="name" header="Nome do Ficheiro" />
            <Column
              field="uploadedAt"
              header="Carregado Em"
              body={(row) =>
                new Date(row.uploadedAt).toLocaleDateString("pt-PT")
              }
            />
            <Column
              field="uploadedBy"
              header="Carregado Por"
              body={(row) => row.uploadedBy || "-"}
            />
            <Column
              header="Ações"
              body={(row) => {
                const isMyDoc = row.uploadedBy === userEmail;
                const isToday =
                  new Date(row.uploadedAt).toDateString() ===
                  new Date().toDateString();
                
                const userCanDelete = canDeleteDocument || (isMyDoc && isToday) || row.id.startsWith("Temp_");

                return (
                  <div className="flex gap-2">
                    {!row.id.startsWith("Temp_") && (
                      <Button
                        icon="pi pi-eye"
                        className="p-button-rounded p-button-text p-button-sm"
                        onClick={() => handleView(row.fileKey, row.name)}
                        tooltip="Visualizar"
                        type="button"
                      />
                    )}
                    {userCanDelete && (
                      <Button
                        icon="pi pi-trash"
                        className="p-button-rounded p-button-text p-button-sm"
                        onClick={() => handleDeleteDoc(row.id)}
                        tooltip="Eliminar"
                        disabled={deleteMutation.isPending}
                        type="button"
                      />
                    )}
                  </div>
                );
              }}
            />
          </DataTable>
        )}

      <DocumentViewerDialog
        visible={viewerVisible}
        title={viewerTitle}
        documentUrl={viewerUrl}
        loading={viewerLoading}
        onHide={() => {
          setViewerVisible(false);
          setViewerUrl(null);
        }}
      />
    </div>
  );
};

export default AttachedDocumentsTab;
